// Onboarding setup-state — derived ENTIRELY from real API data (no hardcoding, no local-only flags).
// This single hook is the source of truth for BOTH the gate (AppShell) and the wizard
// (OnboardingWizard): it answers "is this customer ready to broadcast?" from the same react-query
// caches the rest of the app uses (['accounts'] + ['groups', id]), so any connect / group action
// anywhere immediately updates the gate too.
//
// Ready to broadcast ⇔  a number is CONNECTED  ∧  a CONTROL (sync) group is chosen  ∧  ≥1 TARGET group.

import { useQuery } from '@tanstack/react-query';
import type { AccountSummary } from '@crosscastr/shared';
import { api } from './api.js';

export type SetupStep = 1 | 2 | 3 | 4 | 5 | 6;

export interface WizardGroup {
  jid: string;
  name: string;
  memberCount: number;
}

export interface SetupState {
  loading: boolean;
  accounts: AccountSummary[];
  hasAnyAccount: boolean;
  connectedAccount: AccountSummary | null;
  hasConnectedNumber: boolean;
  /** Number connected but the session hasn't published its groups yet (still loading). */
  requiresSession: boolean;
  groups: WizardGroup[];
  groupsLoaded: boolean;
  syncGroupJid: string | null;
  selectedJids: string[];
  hasControlGroup: boolean;
  hasTargets: boolean;
  /** Everything done — the app unlocks. */
  complete: boolean;
  /** Earliest step that still needs the user — used to RESUME the wizard after a refresh. */
  initialStep: SetupStep;
}

export function useSetupState(): SetupState {
  const accountsQ = useQuery({ queryKey: ['accounts'], queryFn: api.accounts });
  const accounts = accountsQ.data ?? [];
  const connectedAccount = accounts.find((a) => a.status === 'connected') ?? null;
  const connectedId = connectedAccount?.id ?? null;

  const groupsQ = useQuery({
    queryKey: ['groups', connectedId],
    queryFn: () => api.accountGroups(connectedId as string),
    enabled: !!connectedId,
    // Poll only while the live session is still handing over the groups.
    refetchInterval: (q) => (q.state.data?.requiresSession ? 2500 : false),
  });

  const data = groupsQ.data;
  const requiresSession = data?.requiresSession ?? false;
  const groups = data?.groups ?? [];
  const syncGroupJid = data?.syncGroupJid ?? null;
  const selectedJids = data?.selectedJids ?? [];

  const hasConnectedNumber = Boolean(connectedAccount);
  const groupsLoaded = hasConnectedNumber && !requiresSession && groups.length > 0;
  const hasControlGroup = Boolean(syncGroupJid);
  const hasTargets = selectedJids.length > 0;
  const complete = hasConnectedNumber && hasControlGroup && hasTargets;

  const loading = accountsQ.isLoading || (Boolean(connectedId) && groupsQ.isLoading);

  const initialStep: SetupStep = accounts.length === 0
    ? 1
    : !hasConnectedNumber
      ? 2
      : !hasControlGroup
        ? 3
        : !hasTargets
          ? 4
          : 5;

  return {
    loading,
    accounts,
    hasAnyAccount: accounts.length > 0,
    connectedAccount,
    hasConnectedNumber,
    requiresSession,
    groups,
    groupsLoaded,
    syncGroupJid,
    selectedJids,
    hasControlGroup,
    hasTargets,
    complete,
    initialStep,
  };
}
